import { Device } from '@twilio/voice-sdk'
import { reactive } from 'vue'

const SETTINGS = {
	channelName: 'zapo-twilio-channel',
	lsSharedData: 'zapo-shared-call-data',
	lsDeviceLock: 'zapo-device-lock',
	lsTokenBlocked: 'zapo-twilio-token-blocked',
	sessionTabId: 'zapo-tab-id',
	webLockName: 'zapo-twilio-device',
}

// Legacy fallback constants — only used in browsers without the Web Locks API.
const LOCK_TTL_MS = 12000
const LOCK_HEARTBEAT_MS = 4000
const TAKEOVER_JITTER_MS = 350

const ENDED_TOAST_REMOVE_MS = 8000

export default defineNuxtPlugin((nuxtApp) => {
	const tabId = getTabId()
	const channel = typeof BroadcastChannel !== 'undefined'
		? new BroadcastChannel(SETTINGS.channelName)
		: null
	const useWebLocks = typeof navigator !== 'undefined' && 'locks' in navigator

	const twilio = reactive({
		calls: [],
		active: null,
		activeCall: null,
		call: null,
		ui: {
			headerOpen: false,
		},
		isDeviceOwner: false,
		status: 'idle',
		accept,
		end,
		decline: end,
		reject: end,
		toggleMute,
		connect,
		getCall,
	})

	let device = null
	let deviceSetupInProgress = false
	let lockReleaseResolve = null
	let ownershipAcquired = false
	// Legacy fallback state
	let heartbeatTimer = null
	let staleLockWatcher = null
	let twilioToken = null
	const connectionsById = new Map()
	const durationTimersById = new Map()

	initialize()

	return {
		provide: {
			twilio,
		},
	}

	function initialize() {
		loadSharedStateFromStorage()
		setupChannel()
		setupWindowListeners()
		acquireOwnershipRole()
		broadcast({ type: 'request-state' })
	}

	function getTabId() {
		const existing = sessionStorage.getItem(SETTINGS.sessionTabId)
		if (existing) {
			return existing
		}

		const newTabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`
		sessionStorage.setItem(SETTINGS.sessionTabId, newTabId)
		return newTabId
	}

	// ── Ownership ───────────────────────────────────────────────────────────────
	//
	// Primary path: Web Locks API.
	//   navigator.locks.request queues tabs for an exclusive lock.
	//   Only one tab's callback runs at a time. The lock is held for as long as
	//   the returned promise is pending — no heartbeat required, and background
	//   tab throttling has no effect. When a tab closes the browser releases the
	//   lock automatically and the next queued tab wakes up immediately.
	//
	// Fallback path: localStorage heartbeat (browsers without Web Locks API).

	async function acquireOwnershipRole() {
		if (ownershipAcquired) {
			return
		}

		if (shouldBlockTokenRequests()) {
			twilio.status = 'unauthenticated'
			return
		}

		ownershipAcquired = true

		if (useWebLocks) {
			try {
				await navigator.locks.request(
					SETTINGS.webLockName,
					{ mode: 'exclusive' },
					async () => {
						// Re-check auth — we may have waited in the queue for a while.
						if (shouldBlockTokenRequests()) {
							return
						}

						twilio.isDeviceOwner = true
						twilio.status = 'owner'
						await setupDevice()

						// Hold the lock until explicitly released or the tab closes.
						await new Promise((resolve) => {
							lockReleaseResolve = resolve
						})
					}
				)
			} catch (err) {
				if (err?.name !== 'AbortError') {
					console.error('Twilio: lock request failed', err)
				}
			} finally {
				ownershipAcquired = false
			}
		} else {
			tryAcquireDeviceLockLegacy('startup')
			startStaleLockWatcher()
		}
	}

	function releaseDeviceOwnership(broadcastRelease = false) {
		twilio.isDeviceOwner = false
		twilio.status = 'mirror'
		destroyDevice()
		clearDurationTimers()

		if (useWebLocks) {
			if (lockReleaseResolve) {
				lockReleaseResolve()
				lockReleaseResolve = null
			}
		} else {
			stopHeartbeat()
			const lock = readLock()
			if (lock?.tabId === tabId) {
				localStorage.removeItem(SETTINGS.lsDeviceLock)
			}
		}

		if (broadcastRelease) {
			broadcast({ type: 'owner-released', tabId })
		}
	}

	// ── Channel ─────────────────────────────────────────────────────────────────

	function setupChannel() {
		if (!channel) {
			return
		}

		channel.onmessage = (event) => {
			const message = event?.data || {}

			switch (message.type) {
				case 'request-state':
					if (twilio.isDeviceOwner) {
						publishSharedState()
					}
					break
				case 'state-sync':
					if (!twilio.isDeviceOwner) {
						applySharedState(message.data)
					}
					break
				case 'owner-released':
					// With Web Locks the next queued tab wakes up automatically.
					// Only needed for the legacy fallback path.
					if (!useWebLocks) {
						scheduleTakeoverAttempt('owner-released')
					}
					break
				case 'accept-call':
					if (twilio.isDeviceOwner) {
						acceptOnOwner(message.callId)
					}
					break
				case 'end-call':
					if (twilio.isDeviceOwner) {
						endOnOwner(message.callId)
					}
					break
				case 'mute-call':
					if (twilio.isDeviceOwner) {
						toggleMuteOnOwner(message.callId)
					}
					break
				case 'connect-call':
					if (twilio.isDeviceOwner) {
						connectOnOwner(message.number, message.name)
					}
					break
				default:
					break
			}
		}
	}

	// ── Window listeners ────────────────────────────────────────────────────────

	function setupWindowListeners() {
		window.addEventListener('storage', (event) => {
			if (event.key === SETTINGS.lsSharedData && event.newValue && !twilio.isDeviceOwner) {
				try {
					applySharedState(JSON.parse(event.newValue))
				} catch (error) {
					console.error('Twilio: failed to parse shared state', error)
				}
				return
			}

			// Legacy: detect when another tab has taken the lock.
			if (!useWebLocks && event.key === SETTINGS.lsDeviceLock) {
				const lock = readLock()
				const ownerTabId = lock?.tabId || null

				if (ownerTabId && ownerTabId !== tabId) {
					if (twilio.isDeviceOwner) {
						releaseDeviceOwnership(false)
					}
					return
				}

				if (!ownerTabId) {
					scheduleTakeoverAttempt('lock-cleared')
				}
			}

			if (event.key === 'auth') {
				if (isUserLoggedInFromStorage()) {
					clearTokenBlock()
					acquireOwnershipRole()
				} else {
					blockTokenRequests()
					if (twilio.isDeviceOwner) {
						releaseDeviceOwnership(true)
					}
				}
			}
		})

		window.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'visible' && !twilio.isDeviceOwner) {
				if (useWebLocks) {
					// Already queued via Web Locks — just refresh state.
					broadcast({ type: 'request-state' })
				} else {
					tryAcquireDeviceLockLegacy('visible')
				}
			}
		})

		window.addEventListener('beforeunload', () => {
			clearDurationTimers()
			if (!useWebLocks) {
				stopStaleLockWatcher()
				stopHeartbeat()
			}
			if (twilio.isDeviceOwner) {
				releaseDeviceOwnership(true)
			}
		})
	}

	// ── Legacy lock helpers (localStorage heartbeat fallback) ───────────────────

	function startStaleLockWatcher() {
		stopStaleLockWatcher()
		staleLockWatcher = setInterval(() => {
			if (!twilio.isDeviceOwner) {
				const lock = readLock()
				if (!lock || isLockStale(lock)) {
					tryAcquireDeviceLockLegacy('stale-lock')
				}
			}
		}, LOCK_HEARTBEAT_MS)
	}

	function stopStaleLockWatcher() {
		if (!staleLockWatcher) {
			return
		}

		clearInterval(staleLockWatcher)
		staleLockWatcher = null
	}

	function scheduleTakeoverAttempt(reason) {
		if (twilio.isDeviceOwner) {
			return
		}

		setTimeout(() => {
			tryAcquireDeviceLockLegacy(reason)
		}, Math.floor(Math.random() * TAKEOVER_JITTER_MS))
	}

	function readLock() {
		const raw = localStorage.getItem(SETTINGS.lsDeviceLock)
		if (!raw) {
			return null
		}

		try {
			return JSON.parse(raw)
		} catch (error) {
			return null
		}
	}

	function isLockStale(lock) {
		return Date.now() - Number(lock?.heartbeatAt || 0) > LOCK_TTL_MS
	}

	function writeLock() {
		const lockPayload = {
			tabId,
			heartbeatAt: Date.now(),
		}

		localStorage.setItem(SETTINGS.lsDeviceLock, JSON.stringify(lockPayload))
	}

	function tryAcquireDeviceLockLegacy(reason = 'manual') {
		if (shouldBlockTokenRequests()) {
			twilio.status = 'unauthenticated'
			return false
		}

		const lock = readLock()
		const canTakeLock = !lock || lock.tabId === tabId || isLockStale(lock)

		if (!canTakeLock) {
			return false
		}

		writeLock()
		const verify = readLock()

		if (!verify || verify.tabId !== tabId) {
			return false
		}

		if (!twilio.isDeviceOwner) {
			twilio.isDeviceOwner = true
			twilio.status = `owner:${reason}`
			startHeartbeat()
			setupDevice()
		}

		return true
	}

	function startHeartbeat() {
		stopHeartbeat()
		heartbeatTimer = setInterval(() => {
			if (!twilio.isDeviceOwner) {
				stopHeartbeat()
				return
			}

			const existingLock = readLock()
			if (existingLock && existingLock.tabId !== tabId && !isLockStale(existingLock)) {
				releaseDeviceOwnership(false)
				return
			}

			writeLock()
		}, LOCK_HEARTBEAT_MS)
	}

	function stopHeartbeat() {
		if (!heartbeatTimer) {
			return
		}

		clearInterval(heartbeatTimer)
		heartbeatTimer = null
	}

	// ── Token ───────────────────────────────────────────────────────────────────

	async function getTwilioToken() {
		if (shouldBlockTokenRequests()) {
			return null
		}

		try {
			const res = await nuxtApp.$axios.get('/call/token')
			twilioToken = res.token ?? res.data?.token
			clearTokenBlock()
			return twilioToken
		} catch (error) {
			if (isUnauthenticatedError(error)) {
				blockTokenRequests()
				twilio.status = 'unauthenticated'
				return null
			}

			throw error
		}
	}

	// ── Device ──────────────────────────────────────────────────────────────────

	async function setupDevice() {
		if (!twilio.isDeviceOwner || device || deviceSetupInProgress) {
			return
		}

		deviceSetupInProgress = true

		try {
			const token = await getTwilioToken()
			if (!token) {
				releaseDeviceOwnership(false)
				return
			}

			device = new Device(token, {
				codecPreferences: ['opus', 'pcmu'],
			})

			device.on('registered', () => {
				twilio.status = 'ready'
			})

			device.on('error', (error) => {
				twilio.status = 'error'
				console.error('Twilio device error', error)
			})

			device.on('incoming', (connection) => {
				const call = upsertCallFromConnection(connection, {
					status: 'Incoming',
					answered: false,
				})

				attachConnectionEvents(connection, call.id)
				publishSharedState()
			})

			device.on('tokenAboutToExpire', async () => {
				const refreshedToken = await getTwilioToken()
				if (refreshedToken) {
					device.updateToken(refreshedToken)
				}
			})

			device.on('tokenExpired', async () => {
				const refreshedToken = await getTwilioToken()
				if (refreshedToken) {
					device.updateToken(refreshedToken)
				} else {
					releaseDeviceOwnership(true)
				}
			})

			await device.register()
			publishSharedState()
		} catch (error) {
			if (isUnauthenticatedError(error)) {
				blockTokenRequests()
				twilio.status = 'unauthenticated'
				releaseDeviceOwnership(false)
				return
			}

			twilio.status = 'error'
			console.error('Twilio setup failed', error)
			releaseDeviceOwnership(false)
			if (!useWebLocks) {
				scheduleTakeoverAttempt('setup-error')
			}
		} finally {
			deviceSetupInProgress = false
		}
	}

	function isUnauthenticatedError(error) {
		const message = error?.response?.data?.message || error?.data?.message || error?.message
		return message === 'Unauthenticated.'
	}

	function blockTokenRequests() {
		localStorage.setItem(SETTINGS.lsTokenBlocked, '1')
	}

	function clearTokenBlock() {
		localStorage.removeItem(SETTINGS.lsTokenBlocked)
	}

	function isUserLoggedInFromStorage() {
		const authRaw = localStorage.getItem('auth')
		if (!authRaw) {
			return false
		}

		try {
			const auth = JSON.parse(authRaw)
			return auth?.isLoggedIn === true
		} catch (error) {
			return false
		}
	}

	function shouldBlockTokenRequests() {
		const isBlocked = localStorage.getItem(SETTINGS.lsTokenBlocked) === '1'
		if (!isBlocked) {
			return false
		}

		// Auto-clear once auth storage marks the user as logged in.
		if (isUserLoggedInFromStorage()) {
			clearTokenBlock()
			return false
		}

		return true
	}

	function destroyDevice() {
		if (!device) {
			return
		}

		try {
			device.destroy()
		} catch (error) {
			try {
				device.disconnectAll?.()
			} catch (disconnectError) {
				// Best effort teardown.
			}
		}

		device = null
		connectionsById.clear()
	}

	function clearDurationTimers() {
		durationTimersById.forEach((timerId) => clearInterval(timerId))
		durationTimersById.clear()
	}

	// ── Call management ─────────────────────────────────────────────────────────

	function getConnectionMetadata(connection) {
		const metadata = {}
		if (!connection?.customParameters?.forEach) {
			return metadata
		}

		connection.customParameters.forEach((value, key) => {
			metadata[key] = value
		})

		return metadata
	}

	function getConnectionId(connection, metadata = {}) {
		return String(
			connection?.outboundConnectionId ||
			connection?.parameters?.CallSid ||
			metadata.call_sid ||
			metadata.call_id ||
			`${Date.now()}-${Math.random().toString(36).slice(2)}`
		)
	}

	function makeCallShape(partial = {}) {
		const status = partial.status || partial._status || partial.callStatus?.status || 'Incoming'
		const duration = Number(partial.duration || partial.callStatus?.duration || 0)
		const muted = Boolean(partial.muted || partial.callStatus?.muted)
		const name = partial.name || partial.parameters?.Name || partial.Name || ''
		const from = partial.from || partial.parameters?.From || partial.From || ''
		const to = partial.to || partial.parameters?.To || partial.To || ''
		const callSid = partial.callSid || partial.parameters?.CallSid || partial.CallSid || null
		const id = String(partial.id || partial.outboundConnectionId || callSid || partial.call_id || `${Date.now()}`)

		return {
			id,
			outboundConnectionId: partial.outboundConnectionId || id,
			callSid,
			call_id: partial.call_id ?? partial.id ?? null,
			name,
			from,
			to,
			status,
			_status: status,
			answered: Boolean(partial.answered || status === 'Active'),
			muted,
			duration,
			parameters: {
				Name: name,
				From: from,
				To: to,
				CallSid: callSid,
			},
			callStatus: {
				status,
				duration,
				muted,
			},
		}
	}

	function syncCallDerivatives(call) {
		call._status = call.status
		call.callStatus.status = call.status
		call.callStatus.duration = call.duration
		call.callStatus.muted = call.muted
		call.parameters.Name = call.name || ''
		call.parameters.From = call.from || ''
		call.parameters.To = call.to || ''
		call.parameters.CallSid = call.callSid || null
	}

	function findCallIndexById(callId) {
		return twilio.calls.findIndex((call) => String(call.id) === String(callId))
	}

	function upsertCallFromConnection(connection, overrides = {}) {
		const metadata = getConnectionMetadata(connection)
		const id = getConnectionId(connection, metadata)
		const existingIndex = findCallIndexById(id)
		const status = overrides.status || connection?._status || 'Incoming'
		const base = makeCallShape({
			id,
			outboundConnectionId: connection?.outboundConnectionId || id,
			callSid: connection?.parameters?.CallSid || metadata.call_sid || null,
			call_id: metadata.call_id || null,
			name: metadata.name || metadata.Name || '',
			from: metadata.from || metadata.From || '',
			to: metadata.to || metadata.To || '',
			status,
			answered: overrides.answered,
		})

		if (existingIndex === -1) {
			twilio.calls.push(base)
			connectionsById.set(base.id, connection)
			recomputeActiveCall()
			return base
		}

		const existing = twilio.calls[existingIndex]
		existing.outboundConnectionId = base.outboundConnectionId
		existing.callSid = base.callSid
		existing.call_id = base.call_id
		existing.name = base.name || existing.name
		existing.from = base.from || existing.from
		existing.to = base.to || existing.to
		existing.status = base.status || existing.status
		existing.answered = Boolean(base.answered || existing.answered)
		syncCallDerivatives(existing)
		connectionsById.set(existing.id, connection)
		recomputeActiveCall()
		return existing
	}

	function setCallStatus(callId, status, updates = {}) {
		const index = findCallIndexById(callId)
		if (index === -1) {
			return null
		}

		const call = twilio.calls[index]
		call.status = status
		if (Object.prototype.hasOwnProperty.call(updates, 'answered')) {
			call.answered = Boolean(updates.answered)
		}
		if (Object.prototype.hasOwnProperty.call(updates, 'muted')) {
			call.muted = Boolean(updates.muted)
		}
		if (Object.prototype.hasOwnProperty.call(updates, 'duration')) {
			call.duration = Number(updates.duration)
		}
		syncCallDerivatives(call)
		recomputeActiveCall()
		return call
	}

	function recomputeActiveCall() {
		const active = twilio.calls.find((call) => call.answered && call.status === 'Active') || null
		twilio.active = active
		twilio.activeCall = active
		twilio.call = active
		twilio.ui.headerOpen = Boolean(active)
	}

	function removeCallAfterDelay(callId, timeoutMs = ENDED_TOAST_REMOVE_MS) {
		setTimeout(() => {
			const index = findCallIndexById(callId)
			if (index === -1) {
				return
			}

			twilio.calls.splice(index, 1)
			connectionsById.delete(String(callId))
			stopDurationTimer(callId)
			recomputeActiveCall()
			publishSharedState()
		}, timeoutMs)
	}

	function startDurationTimer(callId) {
		stopDurationTimer(callId)
		const intervalId = setInterval(() => {
			const call = twilio.calls.find((item) => String(item.id) === String(callId))
			if (!call || call.status !== 'Active') {
				stopDurationTimer(callId)
				return
			}

			call.duration = Number(call.duration || 0) + 1
			syncCallDerivatives(call)
			publishSharedState(false)
		}, 1000)

		durationTimersById.set(String(callId), intervalId)
	}

	function stopDurationTimer(callId) {
		const key = String(callId)
		const intervalId = durationTimersById.get(key)
		if (!intervalId) {
			return
		}

		clearInterval(intervalId)
		durationTimersById.delete(key)
	}

	function attachConnectionEvents(connection, callId) {
		const onAccepted = () => {
			setCallStatus(callId, 'Active', { answered: true })
			startDurationTimer(callId)
			publishSharedState()
		}

		const onEnded = (status) => {
			setCallStatus(callId, status, { answered: false })
			stopDurationTimer(callId)
			publishSharedState()
			removeCallAfterDelay(callId)
		}

		connection.on('accept', onAccepted)
		connection.on('accepted', onAccepted)
		connection.on('cancel', () => onEnded('Cancelled'))
		connection.on('reject', () => onEnded('Ended'))
		connection.on('disconnect', () => onEnded('Ended'))
		connection.on('error', () => onEnded('Ended'))
	}

	// ── State sharing ───────────────────────────────────────────────────────────

	function buildSharedSnapshot() {
		return {
			calls: twilio.calls.map((call) => makeCallShape(call)),
			activeCallId: twilio.activeCall?.id || null,
			ui: {
				headerOpen: Boolean(twilio.ui.headerOpen),
			},
			updatedAt: Date.now(),
		}
	}

	function publishSharedState(emitBroadcast = true) {
		const snapshot = buildSharedSnapshot()
		localStorage.setItem(SETTINGS.lsSharedData, JSON.stringify(snapshot))
		if (emitBroadcast) {
			broadcast({ type: 'state-sync', data: snapshot })
		}
	}

	function applySharedState(snapshot) {
		if (!snapshot || !Array.isArray(snapshot.calls)) {
			return
		}

		const nextCalls = snapshot.calls.map((call) => makeCallShape(call))
		twilio.calls.splice(0, twilio.calls.length, ...nextCalls)

		const nextActiveId = snapshot.activeCallId
			|| nextCalls.find((call) => call.status === 'Active' && call.answered)?.id
			|| null

		const nextActive = nextActiveId
			? twilio.calls.find((call) => String(call.id) === String(nextActiveId)) || null
			: null

		twilio.active = nextActive
		twilio.activeCall = nextActive
		twilio.call = nextActive
		twilio.ui.headerOpen = Boolean(snapshot.ui?.headerOpen && nextActive)
	}

	function loadSharedStateFromStorage() {
		const raw = localStorage.getItem(SETTINGS.lsSharedData)
		if (!raw) {
			return
		}

		try {
			applySharedState(JSON.parse(raw))
		} catch (error) {
			console.error('Twilio: failed loading shared state', error)
		}
	}

	// ── Public API ──────────────────────────────────────────────────────────────

	function getCallId(input) {
		if (!input) {
			return null
		}

		if (typeof input === 'string' || typeof input === 'number') {
			return String(input)
		}

		return String(
			input.id
			|| input.outboundConnectionId
			|| input.callSid
			|| input.parameters?.CallSid
			|| ''
		)
	}

	function getCall(callId) {
		return twilio.calls.find(
			(call) =>
				String(call.id) === String(callId)
				|| String(call.call_id) === String(callId)
		) || null
	}

	function accept(call) {
		const callId = getCallId(call)
		if (!callId) {
			return false
		}

		if (twilio.isDeviceOwner) {
			return acceptOnOwner(callId)
		}

		broadcast({ type: 'accept-call', callId })
		return true
	}

	function acceptOnOwner(callId) {
		const connection = connectionsById.get(String(callId))
		if (!connection || typeof connection.accept !== 'function') {
			return false
		}

		connection.accept()
		return true
	}

	function end(call = null) {
		const target = call || twilio.activeCall
		const callId = getCallId(target)
		if (!callId) {
			return false
		}

		if (twilio.isDeviceOwner) {
			return endOnOwner(callId)
		}

		broadcast({ type: 'end-call', callId })
		return true
	}

	function endOnOwner(callId) {
		const connection = connectionsById.get(String(callId))
		if (!connection) {
			return false
		}

		const connectionStatus = typeof connection.status === 'function' ? connection.status() : null
		if (typeof connection.ignore === 'function' && connectionStatus === 'pending') {
			connection.ignore()
		} else if (typeof connection.disconnect === 'function') {
			connection.disconnect()
		}

		return true
	}

	function toggleMute(call = null) {
		const target = call || twilio.activeCall
		const callId = getCallId(target)
		if (!callId) {
			return false
		}

		if (twilio.isDeviceOwner) {
			return toggleMuteOnOwner(callId)
		}

		broadcast({ type: 'mute-call', callId })
		return true
	}

	function toggleMuteOnOwner(callId) {
		const call = twilio.calls.find((item) => String(item.id) === String(callId))
		const connection = connectionsById.get(String(callId))
		if (!call || !connection || typeof connection.mute !== 'function') {
			return false
		}

		const nextMuted = !call.muted
		connection.mute(nextMuted)
		setCallStatus(callId, call.status, { muted: nextMuted })
		publishSharedState()
		return true
	}

	function connect(number, name = '') {
		if (twilio.isDeviceOwner) {
			return connectOnOwner(number, name)
		}

		broadcast({ type: 'connect-call', number, name })
		return Promise.resolve(null)
	}

	async function connectOnOwner(number, name = '') {
		if (!device) {
			await setupDevice()
		}
		if (!device) {
			return null
		}

		const connection = await device.connect({
			params: {
				To: number,
				Name: name,
				Direction: 'outbound',
			},
		})

		const call = upsertCallFromConnection(connection, {
			status: 'Calling',
			answered: false,
		})
		attachConnectionEvents(connection, call.id)
		publishSharedState()
		return call
	}

	function broadcast(message) {
		if (!channel) {
			return
		}
		channel.postMessage(message)
	}
})
